import React, { useEffect, useState } from 'react';
import { Card, Alert, Spin, Typography, Descriptions } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { useIsOwner } from '../auth/RoleGuard';
import { getPerJobProfit, formatCents, PerJobProfitResult } from '../../services/moneyApi';

const { Text } = Typography;

interface PerJobProfitProps {
  workOrderId: number;
}

/**
 * Owner-only per-job profit card.
 * Renders nothing for non-owner roles — cost and profit are sensitive.
 * Self-gates via useIsOwner(); no API call is made for non-owners.
 */
const PerJobProfit: React.FC<PerJobProfitProps> = ({ workOrderId }) => {
  const isOwner = useIsOwner();
  const [profit, setProfit] = useState<PerJobProfitResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOwner || !workOrderId) return;
    setLoading(true);
    getPerJobProfit(workOrderId)
      .then(setProfit)
      .catch((err) => {
        console.error('PerJobProfit fetch error:', err);
      })
      .finally(() => setLoading(false));
  }, [isOwner, workOrderId]);

  // Owner gate — renders nothing for non-owner roles
  if (!isOwner) return null;

  const marginPct =
    profit?.marginFraction != null
      ? (profit.marginFraction * 100).toFixed(1) + '%'
      : '—';

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DollarOutlined style={{ color: '#2563eb' }} />
          <span>Job Profit (Owner)</span>
        </div>
      }
      data-testid="perjob-profit"
      style={{ marginTop: 8 }}
    >
      <Spin spinning={loading}>
        {profit && (
          <>
            {profit.hasMissingCost && (
              <Alert
                data-testid="perjob-missing-cost"
                message="Cost data incomplete — profit may be overstated"
                description="One or more parts or the tech labor rate is missing a cost value. Actual profit may be lower."
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Revenue (pre-tax)">
                <Text strong style={{ color: '#16a34a' }}>
                  {formatCents(profit.revenueCents)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Sales tax collected (pass-through)">
                <Text type="secondary">{formatCents(profit.taxCollectedCents)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Part Cost">
                <Text type="danger">− {formatCents(profit.partCostCents)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Labor Cost">
                <Text type="danger">− {formatCents(profit.laborCostCents)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Processing Fee">
                <Text type="danger">− {formatCents(profit.processingFeeCents)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Job Expenses">
                <Text type="danger">− {formatCents(profit.jobExpenseCents)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Net Profit">
                <Text
                  strong
                  style={{ color: profit.profitCents >= 0 ? '#16a34a' : '#dc2626', fontSize: 16 }}
                >
                  {formatCents(profit.profitCents)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Margin">
                <Text strong>{marginPct}</Text>
              </Descriptions.Item>
            </Descriptions>
            {profit.breakdown.laborHours != null && (
              <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                {profit.breakdown.laborHours}h labor × {formatCents(profit.breakdown.techRateCents ?? 0)}/hr
                · {profit.breakdown.partCount} parts
                {profit.breakdown.partsWithMissingCost > 0
                  ? ` (${profit.breakdown.partsWithMissingCost} missing cost)`
                  : ''}
              </Text>
            )}
          </>
        )}
        {!loading && !profit && (
          <Text type="secondary">No profit data available.</Text>
        )}
      </Spin>
    </Card>
  );
};

export default PerJobProfit;
